import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { ScoreInfo } from "@/components/mai/Explain";
import { ASSET_TYPES, MATURITY_LEVELS, CERTAINTY_LEVELS } from "@/components/mai/constants";
import { EvidenceRegisterV2 } from "@/components/mai/EvidenceRegisterV2";
import { DesignCorrections } from "@/components/mai/DesignCorrections";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { CircleNotch, Plus, ArrowsClockwise, Trash, ArrowSquareOut } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ARCH_STATUS = {
  covered: { label: "Covered", color: "#047857" },
  gap: { label: "Gap", color: "#B91C1C" },
  optional_gap: { label: "Optional Gap", color: "#B45309" },
};
const SOURCE_LABEL = { pubmed: "PubMed", clinicaltrials: "ClinicalTrials.gov" };

function CandidateCard({ c, onReviewed }) {
  const [spec, setSpec] = useState({ type: "pivotal_rct", maturity: "published", evidence_certainty: "auto" });
  const [busy, setBusy] = useState(null);

  const act = async (action) => {
    setBusy(action);
    try {
      if (action === "approve") {
        await axios.post(`${API}/literature/candidates/${c.id}/approve`,
          { ...spec, evidence_certainty: spec.evidence_certainty === "auto" ? "" : spec.evidence_certainty });
        toast.success("Candidate approved — now a live evidence asset");
      } else {
        await axios.post(`${API}/literature/candidates/${c.id}/dismiss`);
        toast.success("Candidate dismissed");
      }
      onReviewed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(null); }
  };

  return (
    <div data-testid={`candidate-${c.id}`} className="px-5 py-4 border-b border-slate-100 last:border-0">
      <div className="flex items-start gap-3">
        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-slate-900 text-white shrink-0 mt-0.5">{SOURCE_LABEL[c.source] || c.source}</span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-slate-800 leading-snug">{c.title}</p>
          <p className="text-[11px] text-slate-500 mt-1">{c.authors}</p>
          <p className="font-mono-data text-[10px] text-slate-400 mt-0.5">
            {c.journal}{c.publication_year ? ` · ${c.publication_year}` : ""} · {c.doi_or_registry_id}
            {c.detail ? ` · ${c.detail}` : ""}
          </p>
          <a href={c.source_url} target="_blank" rel="noreferrer" data-testid={`candidate-link-${c.id}`}
            className="inline-flex items-center gap-1 text-[10px] text-blue-700 font-semibold mt-1 hover:underline">
            View source <ArrowSquareOut size={10} />
          </a>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3 pl-8">
        <Select value={spec.type} onValueChange={(v) => setSpec({ ...spec, type: v })}>
          <SelectTrigger data-testid={`candidate-type-${c.id}`} className="h-8 w-52 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {Object.entries(ASSET_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={spec.maturity} onValueChange={(v) => setSpec({ ...spec, maturity: v })}>
          <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {Object.entries(MATURITY_LEVELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={spec.evidence_certainty} onValueChange={(v) => setSpec({ ...spec, evidence_certainty: v })}>
          <SelectTrigger className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="auto">Certainty: auto</SelectItem>
            {Object.entries(CERTAINTY_LEVELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <button data-testid={`candidate-approve-${c.id}`} disabled={!!busy} onClick={() => act("approve")}
          className="bg-slate-900 text-white text-[11px] font-semibold px-3.5 py-2 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5">
          {busy === "approve" && <CircleNotch size={11} className="animate-spin" />} Approve as Asset
        </button>
        <button data-testid={`candidate-dismiss-${c.id}`} disabled={!!busy} onClick={() => act("dismiss")}
          className="text-[11px] font-semibold text-slate-500 border border-slate-300 px-3.5 py-2 hover:bg-slate-50 disabled:opacity-50">
          Dismiss
        </button>
      </div>
    </div>
  );
}

function LiteratureWatch({ productId, onAssetsChanged }) {
  const [feeds, setFeeds] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [form, setForm] = useState({ name: "", terms: "", pubmed: true, clinicaltrials: true });
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => Promise.all([
    axios.get(`${API}/literature/feeds`, { params: { product_id: productId } }).then((r) => setFeeds(r.data)),
    axios.get(`${API}/literature/candidates`, { params: { product_id: productId, status: "pending" } }).then((r) => setCandidates(r.data)),
  ]), [productId]);

  useEffect(() => { load().catch(console.error); }, [load]);

  const addFeed = async () => {
    if (!form.terms.trim()) return toast.error("Search terms are required");
    const sources = [form.pubmed && "pubmed", form.clinicaltrials && "clinicaltrials"].filter(Boolean);
    if (sources.length === 0) return toast.error("Pick at least one source");
    setBusy("add");
    try {
      await axios.post(`${API}/literature/feeds`, { name: form.name, terms: form.terms, product_id: productId, sources });
      toast.success("Feed registered — run a check to discover candidates");
      setForm({ name: "", terms: "", pubmed: true, clinicaltrials: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add feed");
    } finally { setBusy(null); }
  };

  const checkFeed = async (id) => {
    setBusy(id);
    try {
      const r = await axios.post(`${API}/literature/feeds/${id}/check`);
      toast.success(`${r.data.new_candidates} new candidate(s) found`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Check failed");
    } finally { setBusy(null); }
  };

  const removeFeed = async (id) => {
    await axios.delete(`${API}/literature/feeds/${id}`);
    toast.success("Feed removed");
    load();
  };

  return (
    <>
      <section data-testid="literature-feeds" className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200">
          <p className="font-heading font-bold text-sm tracking-tight">Literature & Trial Registry Feeds</p>
          <p className="text-xs text-slate-500 mt-1">Automated discovery from PubMed and ClinicalTrials.gov — new matches enter the review queue below, never the live inventory directly. Checked daily.</p>
        </div>
        {feeds.map((f) => (
          <div key={f.id} data-testid={`feed-row-${f.id}`} className="px-5 py-3 border-b border-slate-100 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-800">{f.name}</p>
              <p className="font-mono-data text-[10px] text-slate-400 mt-0.5">
                “{f.terms}” · {f.sources.map((s) => SOURCE_LABEL[s]).join(" + ")} ·
                {f.last_checked_at ? ` last check ${new Date(f.last_checked_at).toLocaleString()}` : " never checked"} · {f.candidates_total || 0} candidates found
                {f.last_error && <span className="text-red-500"> · {f.last_error}</span>}
              </p>
            </div>
            <button data-testid={`feed-check-${f.id}`} disabled={!!busy} onClick={() => checkFeed(f.id)}
              className="text-[11px] font-semibold text-slate-700 border border-slate-300 px-3 py-1.5 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50">
              {busy === f.id ? <CircleNotch size={11} className="animate-spin" /> : <ArrowsClockwise size={11} />} Check now
            </button>
            <button data-testid={`feed-delete-${f.id}`} onClick={() => removeFeed(f.id)} className="text-slate-300 hover:text-red-600 p-1.5">
              <Trash size={14} />
            </button>
          </div>
        ))}
        <div className="px-5 py-3.5 flex flex-wrap items-center gap-2 bg-slate-50/60">
          <Input data-testid="feed-name-input" className="h-8 w-44 text-xs bg-white" placeholder="Feed name (optional)"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input data-testid="feed-terms-input" className="h-8 w-64 text-xs bg-white" placeholder="Search terms, e.g. Myval transcatheter"
            value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} />
          {["pubmed", "clinicaltrials"].map((s) => (
            <label key={s} className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
              <input type="checkbox" data-testid={`feed-source-${s}`} checked={form[s]} onChange={(e) => setForm({ ...form, [s]: e.target.checked })} />
              {SOURCE_LABEL[s]}
            </label>
          ))}
          <button data-testid="feed-add-btn" disabled={!!busy} onClick={addFeed}
            className="bg-slate-900 text-white text-[11px] font-semibold px-3.5 py-2 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5">
            {busy === "add" ? <CircleNotch size={11} className="animate-spin" /> : <Plus size={11} weight="bold" />} Add feed
          </button>
        </div>
      </section>

      <section data-testid="candidate-queue" className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <p className="font-heading font-bold text-sm tracking-tight">Candidate Evidence — Pending Review</p>
            <p className="text-xs text-slate-500 mt-1">Human validation required: pick the asset type, then approve or dismiss each discovery.</p>
          </div>
          <p className="font-heading font-extrabold text-xl">{candidates.length}</p>
        </div>
        {candidates.length === 0 ? (
          <p className="px-5 py-8 text-center text-xs text-slate-400">No pending candidates — add a feed and run a check.</p>
        ) : candidates.map((c) => (
          <CandidateCard key={c.id} c={c} onReviewed={() => { load(); onAssetsChanged(); }} />
        ))}
      </section>
    </>
  );
}

export default function InventoryPage({ embedded }) {
  const { productId, product } = useProduct();
  const [d, setD] = useState(null);
  const [certaintyFilter, setCertaintyFilter] = useState("all");
  const [incompleteOnly, setIncompleteOnly] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/inventory/${productId}`).then((r) => setD(r.data)).catch(console.error);
  }, [productId]);

  useEffect(() => { setD(null); load(); }, [load]);

  if (!d) return (
    <PageShell embedded={embedded} movedTo={{ label: "Evidence Library", to: "/console/evidence-library" }} pageId="inventory" overline="Evidence Inventory Engine · Layer 2" title="Evidence Inventory">
      <div className="py-32 text-center text-slate-400 text-sm">Building inventory…</div>
    </PageShell>
  );

  const s = d.summary;
  const matches = (a) => (certaintyFilter === "all" || a.evidence_certainty === certaintyFilter) && (!incompleteOnly || !a.citation_complete);

  return (
    <PageShell embedded={embedded} movedTo={{ label: "Evidence Library", to: "/console/evidence-library" }} pageId="inventory" overline={`Evidence Inventory Engine · ${product?.name || ""}`} title="Evidence Inventory">
      <EvidenceRegisterV2 productId={productId} />

      <div data-testid="inventory-summary" className="grid grid-cols-2 md:grid-cols-7 bg-white border border-slate-200 divide-x divide-slate-200">
        {[
          ["Total Assets", s.total_assets],
          ["AI-Sourced", s.ai_sourced],
          ["Citation Incomplete", s.citation_incomplete],
          ["Avg Maturity", s.avg_maturity],
          ["Avg Reuse Score", s.avg_reuse_score],
          ["Provisional Coverage", `${s.provisional_architecture_coverage ?? s.architecture_completeness}%`],
          ["Verified Coverage", `${s.verified_architecture_coverage ?? 0}%`],
        ].map(([label, value]) => (
          <div key={label} className="px-5 py-4" data-testid={label === "Verified Coverage" ? "verified-coverage-tile" : label === "Provisional Coverage" ? "provisional-coverage-tile" : undefined}>
            <p className="overline-label text-slate-400">{label}{label === "Avg Maturity" && <> <ScoreInfo scoreId="maturity_score" /></>}{label === "Avg Reuse Score" && <> <ScoreInfo scoreId="reuse_score" /></>}</p>
            <p className={`font-heading font-extrabold text-2xl mt-1 ${label === "Citation Incomplete" && value > 0 ? "text-amber-600" : label === "Verified Coverage" ? (parseInt(value) >= 70 ? "text-emerald-700" : "text-red-700") : ""}`}>{value}</p>
          </div>
        ))}
      </div>

      {(s.verified_architecture_coverage ?? 0) < (s.provisional_architecture_coverage ?? 0) && (
        <div data-testid="verified-coverage-warning" className="bg-amber-50 border border-amber-300 px-5 py-3 text-xs text-amber-800">
          <span className="font-bold uppercase tracking-wider text-[10px] mr-2">Verified coverage {s.verified_architecture_coverage}%</span>
          Only {s.verified_assets} of {s.total_assets} records are fully verified (complete citation + validated source + reviewer approval).
          Provisional coverage ({s.provisional_architecture_coverage}%) counts unverified records and is not submission-ready. Final conclusions use the verified value.
        </div>
      )}

      <div data-testid="inventory-filters" className="flex flex-wrap items-center gap-2">
        <span className="overline-label text-slate-400 mr-1">Certainty</span>
        {[["all", "All"], ...Object.entries(CERTAINTY_LEVELS).map(([k, v]) => [k, v.label.replace(" certainty", "")])].map(([k, label]) => (
          <button key={k} data-testid={`certainty-filter-${k}`} onClick={() => setCertaintyFilter(k)}
            className={`text-[11px] font-semibold px-3 py-1.5 border transition-colors ${certaintyFilter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
        <button data-testid="citation-incomplete-filter" onClick={() => setIncompleteOnly(!incompleteOnly)}
          className={`ml-3 text-[11px] font-semibold px-3 py-1.5 border transition-colors ${incompleteOnly ? "bg-amber-600 text-white border-amber-600" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
          Citation incomplete only
        </button>
      </div>

      <section data-testid="architecture-table" className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200">
          <p className="font-heading font-bold text-sm tracking-tight">Evidence Architecture Completeness</p>
          <p className="text-xs text-slate-500 mt-1">Every canonical evidence expectation across your markets vs. the assets that can serve it. Provisional counts any record; verified counts only records with a complete citation, validated source and reviewer approval.</p>
        </div>
        {d.architecture.map((r) => {
          const st = ARCH_STATUS[r.status];
          return (
            <div key={r.requirement_type} className="px-5 py-3 border-b border-slate-100 last:border-0 flex items-center gap-4">
              <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 text-white w-24 text-center shrink-0" style={{ background: st.color }}>{st.label}</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-800">{r.requirement_type}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Required in {r.markets_requiring} markets ({r.mandatory_in} mandatory)
                  {r.asset_names.length > 0 && ` · served by: ${r.asset_names.join(", ")}`}
                </p>
              </div>
              <p className="font-mono-data text-sm font-bold shrink-0">{r.assets_covering} <span className="text-[10px] text-slate-400 font-normal">assets</span></p>
              <p data-testid={`verified-covering-${r.requirement_type}`} className={`font-mono-data text-sm font-bold shrink-0 w-24 text-right ${r.verified_assets_covering > 0 ? "text-emerald-700" : "text-red-700"}`}>
                {r.verified_assets_covering ?? 0} <span className="text-[10px] text-slate-400 font-normal">verified</span>
              </p>
            </div>
          );
        })}
      </section>

      <DesignCorrections productId={productId} onAssetsChanged={load} />

      {d.categories.map((cat) => {
        const visible = cat.assets.filter(matches);
        return (
          <section key={cat.id} data-testid={`inventory-category-${cat.id}`} className="bg-white border border-slate-200">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <p className="font-heading font-bold text-sm tracking-tight">{cat.label}</p>
              <p className="font-mono-data text-xs text-slate-400">{visible.length} of {cat.count} assets · avg maturity {cat.avg_maturity}</p>
            </div>
            {visible.length === 0 ? (
              <p className="px-5 py-6 text-center text-xs text-slate-400">{cat.count === 0 ? "No assets in this category." : "No assets match the current filters."}</p>
            ) : visible.map((a) => {
              const cert = CERTAINTY_LEVELS[a.evidence_certainty] || CERTAINTY_LEVELS.moderate;
              return (
                <div key={a.id} data-testid={`inventory-asset-${a.id}`} className="px-5 py-3.5 border-b border-slate-100 last:border-0">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-800 truncate">{a.name}
                        <span data-testid={`certainty-badge-${a.id}`} className={`ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 ${cert.chip}`}>{cert.label}</span>
                        {a.ai_sourced && <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200">AI-Sourced</span>}
                        {a.internal_analysis && <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-500 border border-slate-200">Internal — Not Independently Verifiable</span>}
                        {!a.citation_complete && <span data-testid={`citation-incomplete-${a.id}`} className="ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-300">Citation Incomplete</span>}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{a.type_label} · {a.maturity} · {a.scope}{a.design_label && <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200">{a.design_label}</span>}</p>
                      {a.citation_complete && !a.internal_analysis && (
                        <p className="font-mono-data text-[10px] text-slate-400 mt-0.5 truncate">
                          {a.citation.authors} · {a.citation.journal} ({a.citation.publication_year}) ·{" "}
                          <a href={a.citation.source_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{a.citation.doi_or_registry_id}</a>
                        </p>
                      )}
                      {a.verification && (
                        <div data-testid={`verification-flags-${a.id}`} className="flex flex-wrap items-center gap-1 mt-1.5">
                          <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${a.verification.verified ? "bg-emerald-700 text-white" : "bg-amber-50 text-amber-700 border border-amber-300"}`}>
                            {a.verification.verified ? "Verified" : "Provisional"}
                          </span>
                          {[["content_available", "Content"], ["citation_complete", "Citation"], ["source_verified", "Source"], ["quality_reviewed", "Reviewed"], ["market_acceptable", "Market"], ["submission_usable", "Usable"]].map(([k, label]) => (
                            <span key={k} title={`${label}: ${a.verification[k] ? "yes" : "no"}`}
                              className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${a.verification[k] ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-400 border-slate-200 line-through"}`}>
                              {label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-6 shrink-0 text-right">
                      <div>
                        <p className="overline-label text-slate-400">Maturity</p>
                        <div className="w-20 h-1.5 bg-slate-100 mt-1"><div className="h-full bg-slate-800" style={{ width: `${a.maturity_score}%` }} /></div>
                      </div>
                      <div className="w-28">
                        <p className="overline-label text-slate-400">Applicability</p>
                        <p title={(a.countries_accepting || []).join(", ")}
                          className="font-mono-data text-[10px] text-slate-600 mt-0.5 whitespace-nowrap">
                          {(a.countries_accepting || []).length} accept · {a.adaptation_count} adapt · {a.not_acceptable_count} n/a
                        </p>
                      </div>
                      <div className="w-14">
                        <p className="overline-label text-slate-400">Reuse</p>
                        <p className="font-mono-data text-sm font-bold">{a.reuse_score}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </section>
        );
      })}

      <LiteratureWatch productId={productId} onAssetsChanged={load} />
    </PageShell>
  );
}
